import type { DataJobRunDetail } from '../types/data-jobs.types';
import type { InflationFixSuggestion } from '../types/operations.types';

const FIXABLE_PLATFORMS = new Set(['yahoo_dsp', 'nexxen_dsp']);

function detectMultiple(vendor: number, mart: number): number | null {
  if (!(vendor > 0) || !(mart > 0)) return null;
  const ratio = mart / vendor;
  const nearest = Math.round(ratio);
  if (nearest < 2) return null;
  if (Math.abs(ratio - nearest) / nearest <= 0.02) return nearest;
  return null;
}

export function getInflationFixesFromRun(run: DataJobRunDetail | null | undefined): InflationFixSuggestion[] {
  if (!run || run.job_key !== 'daily_verification') {
    return [];
  }

  const fromMetadata = run.output_metadata?.inflation_fixes;
  if (Array.isArray(fromMetadata) && fromMetadata.length > 0) {
    return (fromMetadata as InflationFixSuggestion[]).filter((fix) => (
      FIXABLE_PLATFORMS.has(fix.platform)
    ));
  }

  const grouped = new Map<string, InflationFixSuggestion & {
    multiplesSet: Set<number>;
    accountsSet: Set<string>;
    metricsSet: Set<string>;
  }>();

  for (const result of run.validation_results) {
    if (result.status !== 'fail' && result.status !== 'failed') continue;
    if (result.check_source !== 'platform_reconciliation') continue;

    const metrics = result.metrics ?? {};
    const platform = String(metrics.platform ?? result.target.split('.')[0] ?? '');
    if (!FIXABLE_PLATFORMS.has(platform)) continue;

    const vendor = Number(metrics.vendor_total);
    const mart = Number(metrics.mart_total);
    const multiple = typeof metrics.inflation_multiple === 'number'
      ? metrics.inflation_multiple
      : detectMultiple(vendor, mart);
    if (multiple == null) continue;

    const activeDate = result.active_date ?? run.active_date;
    if (!activeDate) continue;

    const key = `${platform}:${activeDate}`;
    const existing = grouped.get(key);
    if (!existing) {
      grouped.set(key, {
        platform,
        active_date: activeDate,
        multiples: [multiple],
        accounts: [String(metrics.account_id ?? result.target.split('.')[1] ?? '')].filter(Boolean),
        metrics: [result.check_type],
        summary: '',
        multiplesSet: new Set([multiple]),
        accountsSet: new Set(
          [String(metrics.account_id ?? result.target.split('.')[1] ?? '')].filter(Boolean),
        ),
        metricsSet: new Set([result.check_type]),
      });
      continue;
    }

    existing.multiplesSet.add(multiple);
    if (metrics.account_id) existing.accountsSet.add(String(metrics.account_id));
    existing.metricsSet.add(result.check_type);
  }

  return Array.from(grouped.values()).map((entry) => {
    const multiples = Array.from(entry.multiplesSet).sort((a, b) => a - b);
    const accounts = Array.from(entry.accountsSet).sort();
    return {
      platform: entry.platform,
      active_date: entry.active_date,
      multiples,
      accounts,
      metrics: Array.from(entry.metricsSet).sort(),
      summary: (
        `${entry.platform} on ${entry.active_date} looks like `
        + `${multiples.map((value) => `${value}x`).join('/')} raw-append inflation `
        + `across ${accounts.length} account(s).`
      ),
    };
  });
}
